<template>
  <div
    class="qmulus-carousel flex-column-section"
    data-qa="qmulus-carousel"
    @keydown="onKeydownWithin"
  >
    <!--
      All slides are rendered on top of each other, so that the carousel keeps the height of the
      tallest slide and the dots do not move while switching between slides.
    -->
    <div class="qmulus-carousel__slides">
      <div
        v-for="(slideNode, index) in getSlideNodes()"
        :key="slideNode.key ?? index"
        class="qmulus-carousel__slide"
        :class="{
          'qmulus-carousel__slide_hidden': index !== modelValue,
        }"
        :inert="index !== modelValue"
        :aria-hidden="index !== modelValue ? 'true' : undefined"
        data-qa="qmulus-carousel__slide"
        :data-qa-id="index"
      >
        <component :is="slideNode" />
      </div>
    </div>
    <div
      v-if="renderedSlideCount > 1"
      class="flex-row flex-row_center"
      data-qa="qmulus-carousel__dots"
    >
      <button
        v-for="(_slideNumber, index) in renderedSlideCount"
        :key="index"
        type="button"
        class="qmulus-carousel__dot"
        :class="{
          'qmulus-carousel__dot_active': index === modelValue,
        }"
        :aria-label="t('showSlide', {
          number: index + 1,
          count: renderedSlideCount,
        })"
        :aria-current="index === modelValue ? 'true' : undefined"
        data-qa="qmulus-carousel__dot"
        :data-qa-id="index"
        @click="emit('update:modelValue', index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  type VNode,
  onBeforeUnmount,
  onMounted,
  useSlots,
} from 'vue';

import {
  EKeyboardKey,
} from '../../constants/keyboard.ts';
import useLibraryI18n from '../../i18n/composable.ts';
import {
  getSlotChildren,
} from '../../utils/vue.ts';

const props = defineProps<{
  /** Index of the shown slide. */
  modelValue: number,
  /**
   * The arrow keys switch the slides wherever the focus is while the carousel is mounted, e.g. in
   * a modal whose only content it is. Otherwise only while the focus is within the carousel.
   */
  hasGlobalKeyboardNavigation?: boolean,
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void,
}>();

defineSlots<{
  /** Every child is one slide, e.g. one element per item of a `v-for`. */
  default(): any,
}>();

const slots = useSlots();

/**
 * The number of slides of the latest render, set when the slides are rendered. The dots below
 * them read it in the same render, and the keyboard handler, which runs outside of rendering where
 * the slot must not be called, reads it as well.
 */
let renderedSlideCount = 0;

function getSlideNodes(): VNode[] {
  const slideNodes = getSlotChildren(slots.default?.() ?? []);
  renderedSlideCount = slideNodes.length;
  return slideNodes;
}

const {
  t,
} = useLibraryI18n('components/carousel/qmulus-carousel');

/** Fields where the arrow keys move the cursor or change the value instead. */
const EDITABLE_ELEMENT_SELECTOR = 'input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]';

function showSlideOnArrowKey(event: KeyboardEvent) {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return;
  }
  if (event.target instanceof Element && event.target.closest(EDITABLE_ELEMENT_SELECTOR) !== null) {
    return;
  }
  if (renderedSlideCount < 2) {
    return;
  }
  // Both directions wrap around, from the last slide to the first and back.
  if (event.key === EKeyboardKey.ArrowLeft) {
    emit('update:modelValue', (props.modelValue - 1 + renderedSlideCount) % renderedSlideCount);
    event.preventDefault();
  } else if (event.key === EKeyboardKey.ArrowRight) {
    emit('update:modelValue', (props.modelValue + 1) % renderedSlideCount);
    event.preventDefault();
  }
}

function onKeydownWithin(event: KeyboardEvent) {
  // The window listener handles these already.
  if (!props.hasGlobalKeyboardNavigation) {
    showSlideOnArrowKey(event);
  }
}

onMounted(() => {
  if (props.hasGlobalKeyboardNavigation) {
    window.addEventListener('keydown', showSlideOnArrowKey);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', showSlideOnArrowKey);
});
</script>

<style scoped lang="scss">
.qmulus-carousel {
  &__slides {
    display: grid;
  }

  &__slide {
    grid-area: 1 / 1;
    min-width: 0;

    &_hidden {
      visibility: hidden;
    }
  }

  &__dot {
    width: 0.75rem;
    height: 0.75rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: var(--border-color-strong);
    cursor: pointer;

    &_active {
      background-color: var(--primary-color);
    }

    &:focus-visible {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }
  }
}
</style>
